import React, { FC } from 'react';
import { Route, Switch } from 'react-router-dom';
import { DealerArea, DealerForm, DealerFullModel } from '../../../../shared/dealer';
import { LinkButton, PageHeader, UserRoute, UserStateComponent } from '../../../components';
import { useTranslation } from '../../../translations';
import {
  Fetcher,
  isAccessError,
  isNonEmpty,
  isResourceError,
  NonEmptyList,
  useConfig,
  useConvention,
  useFetcher,
  useUserSafe,
} from '../../../utils';
import { AreaSelection } from './AreaSelection';
import { DealerBan } from './DealerBan';
import { DealerButtons } from './DealerButtons';
import { DealerFormComponent } from './DealerForm';
import { DealersClosed } from './DealersClosed';
import { SpaceApplicationForm } from './SpaceApplicationForm';
import { VendorAssistantComponent } from './VendorAssistant';
import { VendorAssistantConfirmComponent } from './VendorAssistantConfirm';
import { VendorSeatingComponent } from './VendorSeating';

interface LoadingData {
  readonly dealer: DealerFullModel | undefined;
  readonly prevDealer: DealerFullModel | undefined;
  readonly form: DealerForm;
}

export const ApplyDealerArea: FC = () => {
  return (
    <UserStateComponent>
      <ApplyDealerAreaInner />
    </UserStateComponent>
  );
};

const ApplyDealerAreaInner: FC = () => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const { dealersAreas } = useConfig();
  const { enableDealers } = useConvention();

  const fetcher = useFetcher(async () => {
    if (!isNonEmpty(dealersAreas) || !enableDealers) {
      return undefined;
    }

    return await fetchDealerData(user.id);
  }, [user.id, dealersAreas, enableDealers]);

  if (!enableDealers) {
    return <DealersClosed noAreas={false} />;
  }

  if (!fetcher.success) {
    if (fetcher.error && isAccessError(fetcher.error)) {
      return <DealerBan />;
    }

    return <Fetcher result={fetcher} />;
  }

  const { data } = fetcher;

  if (!data || !isNonEmpty(dealersAreas)) {
    // No areas available
    return <DealersClosed noAreas />;
  }

  return (
    <>
      <PageHeader>{ts('dealer_application')}</PageHeader>
      <DealerRouter
        dealer={data.dealer}
        dealerAreas={dealersAreas}
        form={data.form}
        prevDealer={data.prevDealer}
        refresh={fetcher.refresh}
      />
    </>
  );
};

export interface DealerLoadYears {
  prevDealer?: DealerFullModel;
  dealer?: DealerFullModel;
}

export async function fetchDealerData(userId: number): Promise<LoadingData> {
  const form = await api.getDealerForm();
  let dealer: DealerFullModel | undefined;
  let prevDealer: DealerFullModel | undefined;
  try {
    dealer = await api.getUserDealer(userId);
  } catch (error) {
    if (!isResourceError(error)) {
      throw error;
    }
  }

  if (!dealer) {
    try {
      prevDealer = await api.getUserPreviousDealer(userId);
    } catch {
      // Do nothing
    }
  }

  return {
    dealer,
    prevDealer,
    form,
  };
}

interface DealerRouterProps extends LoadingData {
  readonly refresh: () => void;
  readonly dealerAreas: NonEmptyList<DealerArea>;
}

const DealerRouter: FC<DealerRouterProps> = (props) => {
  const user = useUserSafe();
  const { dealer, form, dealerAreas, prevDealer, refresh } = props;

  const { ts } = useTranslation();
  return (
    <Switch>
      <Route
        exact
        path="/vendor/edit"
        render={() => (
          <DealerFormComponent
            callback={refresh}
            dealer={dealer}
            form={form}
            prevDealer={prevDealer}
            user={user}
          />
        )}
      />
      <Route
        exact
        path="/vendor"
        render={() => {
          if (!dealer) {
            return (
              <DealerFormComponent
                callback={refresh}
                form={form}
                prevDealer={prevDealer}
                user={user}
              />
            );
          }

          if (dealerAreas.length > 1) {
            return <AreaSelection dealer={dealer} form={form} />;
          }

          return (
            <>
              <DealerButtons dealer={dealer} dealerAssistantLimit={form.dealerAssistantLimit} />
              <SpaceApplicationForm
                callback={refresh}
                dealer={dealer}
                dealerAreaId={dealerAreas[0].id}
              />
            </>
          );
        }}
      />
      {form.dealerAssistantLimit > 0 && (
        <UserRoute
          exact
          path="/vendor/assistants"
          render={() => (
            <VendorAssistantComponent
              dealer={dealer}
              dealerAssistantLimit={form.dealerAssistantLimit}
            />
          )}
        />
      )}
      {form.dealerAssistantLimit > 0 && (
        <UserRoute
          component={VendorAssistantConfirmComponent}
          exact
          path="/vendor/assistants/confirm"
        />
      )}
      <UserRoute
        exact
        path="/vendor/seating"
        render={() => <VendorSeatingComponent dealer={dealer} />}
        requirePermissions="system:vendor"
      />
      {dealer && (
        <Route
          exact
          path="/vendor/space/:id"
          render={(route) => (
            <>
              <LinkButton color="secondary" to="/vendor">
                {ts('common.go_back')}
              </LinkButton>
              <hr />
              <SpaceApplicationForm
                callback={refresh}
                dealer={dealer}
                dealerAreaId={Number.parseInt((route.match.params as { id: string }).id, 10)}
              />
            </>
          )}
        />
      )}
    </Switch>
  );
};
